import i18next from 'i18next'
import { registerTranslation } from '@nordhealth/components'
import {
  type WeekDayISONumbers,
  type WeekDayJSNumbers,
  type WeekDaySettingValues,
  weekdaySettingsOptions,
} from '@/utils/date/getter.js'
import { useAuth } from '@/composables/useAuth.js'
import { useNordTranslation } from '@/composables/useNordTranslation.js'
import { keyBy } from '@/utils/array.js'
import { TimeFormat, type TimeFormats } from '@/utils/time.js'
/* current locale */

const currentLocale = ref('en-US')
const dir = computed(() => i18next.dir(currentLocale.value))

watch(currentLocale, async (newLocale) => {
  await loadTranslations(newLocale)

  return i18next.changeLanguage(newLocale)
})

i18next.on('languageChanged', (newLocale) => {
  if (newLocale !== currentLocale.value) {
    currentLocale.value = newLocale
  }
})

/**
 * Load application translations from a i18next JSON v4 file for the
 * given locale.
 */
const loadTranslations = async (locale: string) => {
  if (!i18next.hasResourceBundle(locale, 'application')) {
    // load application resource bundle with dynamic import
    const messages = await import(`@/locales/${locale}/application.json`)

    // set locale messages
    i18next.addResourceBundle(locale, 'application', messages.default)

    // generate Nord Translation using the locale messages
    const nordTranslation = useNordTranslation(locale)

    // register Nord Translation
    registerTranslation(nordTranslation)
  }

  return nextTick()
}

export const useCurrentDir = () => {
  return dir
}

export const useCurrentLocale = () => {
  return currentLocale
}

export const useCurrentFullCalendarLocale = () => {
  return supportedLocales.find((locale) => locale.code === currentLocale.value)
    ?.fullCalendarLocale
}

/**
 * Gets the first day of the week for the current user's locale/settings.
 *
 * Returns the weekday number in either ISO 8601 or JavaScript format:
 * - ISO 8601: 1 = Monday, 2 = Tuesday, ..., 7 = Sunday (used by Luxon)
 * - JavaScript: 0 = Sunday, 1 = Monday, ..., 6 = Saturday (used by Date.getDay() from JavaScript API)
 *
 * The function prioritizes user settings over locale defaults, ensuring consistent
 * behavior across the application.
 *
 * @param format - The format to return the weekday in:
 *   - `'iso'`: ISO 8601 format (1-7, Mon-Sun) - default
 *   - `'js'`: JavaScript format (0-6, Sun-Sat)
 * @returns The first day of the week number in the specified format, or undefined if not available
 *
 * @example
 * ```typescript
 * // Get ISO format (default) - Monday = 1, Sunday = 7
 * useCurrentLocaleFirstDayOfWeek() // Returns: WeekDayISONumbers | undefined
 * useCurrentLocaleFirstDayOfWeek('iso') // Returns: WeekDayISONumbers | undefined
 *
 * // Get JavaScript format - Sunday = 0, Monday = 1
 * useCurrentLocaleFirstDayOfWeek('js') // Returns: WeekDayJSNumbers | undefined
 * ```
 */
export function useCurrentLocaleFirstDayOfWeek(
  format?: 'iso',
): WeekDayISONumbers | undefined
export function useCurrentLocaleFirstDayOfWeek(
  format: 'js',
): WeekDayJSNumbers | undefined
export function useCurrentLocaleFirstDayOfWeek(
  format: 'iso' | 'js' = 'iso',
): WeekDayISONumbers | WeekDayJSNumbers | undefined {
  const { getSetting } = useAuth()

  // 1. Try to get from user's organization settings
  const settingFirstDayOfWeek = getSetting('first_day_of_week') as
    | WeekDaySettingValues
    | undefined
  const isoFromSettings = settingFirstDayOfWeek
    ? weekdaySettingsOptions[settingFirstDayOfWeek] // Already in ISO format (1-7)
    : undefined

  // 2. Fall back to locale-specific default
  const localeConfig = supportedLocales.find(
    (locale) => locale.code === currentLocale.value,
  )
  const localeFirstDayOfWeek = localeConfig?.firstDayOfWeek

  // Convert locale's firstDayOfWeek to ISO 8601 format (1-7)
  // Some locales use JS format (0-6), so we need to convert Sunday (0) to 7
  const isoFromLocale =
    localeFirstDayOfWeek === null || localeFirstDayOfWeek === undefined
      ? undefined
      : localeFirstDayOfWeek === 0
        ? 7 // Sunday in JS format (0) → Sunday in ISO format (7)
        : localeFirstDayOfWeek // Monday-Saturday (1-6) are the same in both formats

  // Prioritize user settings over locale defaults
  const isoWeekday = isoFromSettings ?? isoFromLocale

  // Convert to requested format
  if (format === 'js' && isoWeekday) {
    // Convert ISO (1-7) to JavaScript (0-6): Monday(1)→1, ..., Sunday(7)→0
    return (isoWeekday % 7) as WeekDayJSNumbers
  }

  return isoWeekday as WeekDayISONumbers | undefined
}

export const useCurrentLocaleTimeFormat = () => {
  return supportedLocales.find((locale) => locale.code === currentLocale.value)
    ?.timeFormat
}

export const useCurrentLocaleDateFormat = () => {
  return supportedLocales.find((locale) => locale.code === currentLocale.value)
    ?.dateFormat
}

/* supported locales */
export type SupportedLocaleDateFormats =
  | 'd/M/yyyy'
  | 'M/d/yyyy'
  | 'd.M.yyyy'
  | 'dd.MM.yyyy'
  | 'dd-MM-yyyy'
  | 'dd/MM/yyyy'
  | 'MM/dd/yyyy'
  | 'yyyy/MM/dd'
  | 'yyyy-MM-dd'
  | 'yyyy.MM.dd'
const supportedLocales: Array<{
  code: string
  name: string
  zendeskLocale: string
  backendLocale: string
  fullCalendarLocale: string
  firstDayOfWeek: 0 | 1
  timeFormat: TimeFormats
  dateFormat:
    | 'd/M/yyyy'
    | 'M/d/yyyy'
    | 'd.M.yyyy'
    | 'dd.MM.yyyy'
    | 'dd-MM-yyyy'
    | 'dd/MM/yyyy'
    | 'MM/dd/yyyy'
    | 'yyyy/MM/dd'
    | 'yyyy-MM-dd'
    | 'yyyy.MM.dd'
}> = [
  {
    code: 'ca-ES',
    name: 'Catalan',
    zendeskLocale: 'ca',
    backendLocale: 'ca',
    fullCalendarLocale: 'ca',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'zh-TW',
    name: 'Chinese Traditional',
    zendeskLocale: 'zh-tw',
    backendLocale: 'zh-cn',
    fullCalendarLocale: 'zh-cn',
    firstDayOfWeek: 0, // Sunday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'yyyy-MM-dd',
  },
  {
    code: 'cs-CZ',
    name: 'Czech',
    zendeskLocale: 'cs',
    backendLocale: 'cs',
    fullCalendarLocale: 'cs',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'da-DK',
    name: 'Danish',
    zendeskLocale: 'da',
    backendLocale: 'da',
    fullCalendarLocale: 'da',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'nl-NL',
    name: 'Dutch',
    zendeskLocale: 'nl',
    backendLocale: 'nl',
    fullCalendarLocale: 'nl',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'en-GB',
    name: 'English, United Kingdom',
    zendeskLocale: 'en-gb',
    backendLocale: 'en-gb',
    fullCalendarLocale: 'en-gb',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'en-US',
    name: 'English, United States',
    zendeskLocale: 'en-us',
    backendLocale: 'en-us',
    fullCalendarLocale: 'en',
    firstDayOfWeek: 0, // Sunday
    timeFormat: TimeFormat['h:mm a'],
    dateFormat: 'MM/dd/yyyy',
  },
  {
    code: 'et-EE',
    name: 'Estonian',
    zendeskLocale: 'et',
    backendLocale: 'et',
    fullCalendarLocale: 'et',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['H.mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'fi-FI',
    name: 'Finnish',
    zendeskLocale: 'fi',
    backendLocale: 'fi',
    fullCalendarLocale: 'fi',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['H.mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'fr-FR',
    name: 'French',
    zendeskLocale: 'fr-fr',
    backendLocale: 'fr',
    fullCalendarLocale: 'fr',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'de-DE',
    name: 'German',
    zendeskLocale: 'de',
    backendLocale: 'de',
    fullCalendarLocale: 'de',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'he-IL',
    name: 'Hebrew',
    zendeskLocale: 'he',
    backendLocale: 'he',
    fullCalendarLocale: 'he',
    firstDayOfWeek: 0, // Sunday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'hu-HU',
    name: 'Hungarian',
    zendeskLocale: 'hu',
    backendLocale: 'hu',
    fullCalendarLocale: 'hu',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'yyyy.MM.dd',
  },
  {
    code: 'is-IS',
    name: 'Icelandic',
    zendeskLocale: 'is',
    backendLocale: 'is',
    fullCalendarLocale: 'is',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'it-IT',
    name: 'Italian',
    zendeskLocale: 'it',
    backendLocale: 'it',
    fullCalendarLocale: 'it',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'lv-LV',
    name: 'Latvian',
    zendeskLocale: 'lv',
    backendLocale: 'lv',
    fullCalendarLocale: 'lv',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'lt-LT',
    name: 'Lithuanian',
    zendeskLocale: 'lt',
    backendLocale: 'lt',
    fullCalendarLocale: 'lt',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'no-NO',
    name: 'Norwegian',
    zendeskLocale: 'no',
    backendLocale: 'nb',
    fullCalendarLocale: 'nb',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'pl-PL',
    name: 'Polish',
    zendeskLocale: 'pl',
    backendLocale: 'pl',
    fullCalendarLocale: 'pl',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'pt-PT',
    name: 'Portuguese',
    zendeskLocale: 'pt',
    backendLocale: 'pt',
    fullCalendarLocale: 'pt',
    firstDayOfWeek: 0, // Sunday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'ro-RO',
    name: 'Romanian',
    zendeskLocale: 'ro',
    backendLocale: 'ro',
    fullCalendarLocale: 'ro',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd.MM.yyyy',
  },
  {
    code: 'ru-RU',
    name: 'Russian',
    zendeskLocale: 'ru',
    backendLocale: 'ru',
    fullCalendarLocale: 'ru',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'es-ES',
    name: 'Spanish',
    zendeskLocale: 'es-es',
    backendLocale: 'es',
    fullCalendarLocale: 'es',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
  {
    code: 'sv-SE',
    name: 'Swedish',
    zendeskLocale: 'sv',
    backendLocale: 'sv',
    fullCalendarLocale: 'sv',
    firstDayOfWeek: 1, // Monday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'yyyy-MM-dd',
  },
  {
    code: 'th-TH',
    name: 'Thai',
    zendeskLocale: 'th',
    backendLocale: 'th',
    fullCalendarLocale: 'th',
    firstDayOfWeek: 0, // Sunday
    timeFormat: TimeFormat['HH:mm'],
    dateFormat: 'dd/MM/yyyy',
  },
]

const supportedLocalesByCode = keyBy(supportedLocales, (p) => p.code)
const supportedLocalesByBackendLocale = keyBy(
  supportedLocales,
  (p) => p.backendLocale,
)

function convertBackendLocaleToFrontendLocale(backendLocale: string) {
  return supportedLocalesByBackendLocale[backendLocale]?.code ?? 'en-US'
}

export const useSupportedLocales = () => ({
  supportedLocales,
  supportedLocalesByCode,
  supportedLocalesByBackendLocale,
  convertBackendLocaleToFrontendLocale,
})
